/**
 * Zod schemas for MCP tool validation
 * Shared between local MCP server and remote Cloudflare Worker
 *
 * Updated for Hybrid Storage Architecture (no inline data)
 */
import { z } from 'zod';
import { validateDatasetName } from '@deckspace/validation';
import { columnSchemaUnion } from '@deckspace/field-schemas';
import { workspaceIdSchema, pageIdSchema, surfaceIdSchema } from '@deckspace/ids';
// ============================================================================
// Mermaid Style Schema (used by mermaid mapping)
// ============================================================================
// Mermaid design preview styling schema
export const mermaidStyleSchema = z
    .object({
    background: z.string().optional(),
    nodeColors: z
        .object({
        primary: z.string().optional(),
        primaryText: z.string().optional(),
        primaryBorder: z.string().optional(),
        secondary: z.string().optional(),
        secondaryText: z.string().optional(),
        secondaryBorder: z.string().optional(),
        tertiary: z.string().optional(),
        tertiaryText: z.string().optional(),
        tertiaryBorder: z.string().optional(),
    })
        .optional(),
    fontFamily: z.string().optional(),
    lineColor: z.string().optional(),
    labelBackground: z.string().optional(),
})
    .optional();
// ============================================================================
// Fragment Schemas (reusable building blocks matching @deckspace/types)
// ============================================================================
/** Single annotation (for use in tool params) */
export const annotationSchema = z.object({
    text: z.string().describe('Annotation text'),
    author: z.enum(['human', 'ai']).optional(),
});
/** Array of annotations, optional (for tool input fields) */
export const annotationsSchema = z.array(annotationSchema).optional();
// Workspace metadata schema
export const workspaceMetadataSchema = z.object({
    purpose: z.string().optional().describe('What this workspace is for'),
    context: z.string().optional().describe('Background context for the workspace'),
    tags: z.array(z.string()).optional().describe('Tags for categorization'),
});
export const workspaceInputSchema = z.object({
    action: z
        .enum(['create', 'rename', 'update_metadata', 'delete'])
        .describe('The workspace operation to perform'),
    id: workspaceIdSchema
        .optional()
        .describe('Workspace ID — required for rename, update_metadata, delete'),
    name: z.string().min(1).optional().describe('Workspace name — required for create and rename'),
    metadata: workspaceMetadataSchema
        .optional()
        .describe('Workspace metadata — optional for create, required for update_metadata'),
});
export const pageInputSchema = z.object({
    action: z
        .enum(['create', 'rename', 'delete', 'reorder'])
        .describe('The page operation to perform'),
    id: pageIdSchema.optional().describe('Page ID — required for rename, delete'),
    workspaceId: workspaceIdSchema
        .optional()
        .describe('Workspace ID — required for create and reorder'),
    name: z.string().min(1).optional().describe('Page name — required for create and rename'),
    sortOrder: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe('Display order (0-based) — optional for create, defaults to last position'),
    order: z
        .array(pageIdSchema)
        .optional()
        .describe('Array of page IDs in desired order — required for reorder'),
});
/** Matches FilterOperator from @deckspace/types */
const filterOperatorSchema = z.enum([
    '=',
    '!=',
    '<',
    '>',
    '<=',
    '>=',
    'LIKE',
    'IN',
    'IS NULL',
    'IS NOT NULL',
]);
/** Matches WhereFilter from @deckspace/types */
export const whereFilterSchema = z.object({
    column: z.string(),
    op: filterOperatorSchema,
    value: z
        .union([
        z.string(),
        z.number(),
        z.boolean(),
        z.null(),
        z.array(z.union([z.string(), z.number()])),
    ])
        .optional(),
});
/** Matches OrderByClause from @deckspace/types */
export const orderByClauseSchema = z.object({
    column: z.string(),
    direction: z.enum(['ASC', 'DESC']).optional(),
});
/** Matches DatasetQueryConfig from @deckspace/types. Optional as a whole. */
export const queryConfigSchema = z
    .object({
    columns: z.array(z.string()).optional(),
    where: z.array(whereFilterSchema).optional(),
    orderBy: z.array(orderByClauseSchema).optional(),
    limit: z.number().positive().optional(),
})
    .optional();
/** Matches VisualizationDisplayConfig subset (title, subtitle, timezone, intent). Optional as a whole. */
export const vizConfigSchema = z
    .object({
    title: z.string().optional(),
    subtitle: z.string().optional(),
    timezone: z.string().optional(),
    intent: z.enum(['neutral', 'positive-negative', 'comparison', 'diverging']).optional(),
})
    .optional();
// ============================================================================
// Data Tool Schemas
// ============================================================================
/**
 * Reusable dataset name schema with full validation.
 * Delegates to validateDatasetName from @deckspace/validation which checks:
 * - Starts with a letter, alphanumeric + underscores only
 * - Not a SQL reserved word (50+ keywords)
 * - Max 64 characters
 * - No system dataset prefix (_)
 */
const datasetNameSchema = z
    .string()
    .min(1)
    .superRefine((val, ctx) => {
    try {
        validateDatasetName(val);
    }
    catch (e) {
        ctx.addIssue({
            code: 'custom',
            message: e instanceof Error ? e.message : 'Invalid dataset name',
        });
    }
});
/**
 * Column schema entry — discriminated by type.
 * Schemas defined in @deckspace/field-schemas (shared contract).
 */
const columnSchemaEntry = columnSchemaUnion;
/**
 * Data input schema — flat object with optional mode-specific fields.
 * Field-combination validation (e.g. insert requires rows) is in the handler.
 *
 * Flat shape required: MCP SDK's normalizeObjectSchema needs .shape to emit
 * full JSON Schema. z.discriminatedUnion lacks .shape → empty schema to Claude.
 */
export const dataInputSchema = z
    .object({
    action: z.enum(['create', 'insert', 'update', 'delete_rows', 'delete_dataset', 'schema']),
    workspaceId: workspaceIdSchema,
    dataset: datasetNameSchema,
    // create + insert: rows to validate and insert
    rows: z.array(z.record(z.string(), z.unknown())).optional(),
    // create + schema: column type definitions
    schema: z
        .record(z.string(), columnSchemaEntry)
        .optional()
        .describe('Column schema. Map column names to { type, ... }. Required for create and schema actions. Types: text, number, date, boolean, url, percent, currency.'),
    // update / delete_rows
    where: z.array(whereFilterSchema).optional(),
    set: z.record(z.string(), z.unknown()).optional(),
})
    .passthrough();
// ============================================================================
// Relationship Tool Schemas
// ============================================================================
/**
 * Single relationship schema
 */
export const relationshipSchema = z.object({
    from: z.string().min(1).describe('Source reference (dataset:id)'),
    to: z.string().min(1).describe('Target reference (dataset:id)'),
    type: z.string().min(1).describe('Relationship type'),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
// ============================================================================
// State Tool Schemas
// ============================================================================
/**
 * deckspace_list — browse workspace hierarchy (summaries only)
 */
export const listInputSchema = z
    .object({
    workspaceId: workspaceIdSchema
        .optional()
        .describe('Workspace to browse. Omit to list all workspaces.'),
    pageId: pageIdSchema
        .optional()
        .describe('Page to browse. Returns item summaries. Takes precedence over workspaceId.'),
})
    .optional()
    .describe('Omit entirely to list all workspaces.');
/**
 * deckspace_detail — full payload for one entity
 */
export const detailInputSchema = z
    .object({
    itemId: surfaceIdSchema
        .optional()
        .describe('Surface ID. Takes precedence over dataset params.'),
    workspaceId: workspaceIdSchema.optional().describe('Required when querying a dataset.'),
    dataset: datasetNameSchema.optional().describe('Dataset name. Requires workspaceId.'),
    where: z
        .array(whereFilterSchema)
        .optional()
        .describe('Row filters. Only applies to dataset queries.'),
    limit: z
        .number()
        .int()
        .min(1)
        .max(50)
        .optional()
        .default(5)
        .describe('Max rows to return (default 5, max 50). Only applies to dataset queries.'),
})
    .refine((d) => d.itemId || (d.workspaceId && d.dataset), {
    message: 'Provide itemId, or workspaceId + dataset.',
});
// ============================================================================
// Validation Functions
// ============================================================================
/**
 * Validate dataset name format.
 * Delegates to validateDatasetName from @deckspace/validation for
 * reserved word checks, length limits, and system prefix validation.
 */
export function isValidDatasetName(name) {
    try {
        validateDatasetName(name);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Validate reference format (dataset:id)
 */
export function isValidReference(ref) {
    const parts = ref.split(':');
    if (parts.length !== 2)
        return false;
    const [dataset, id] = parts;
    return dataset !== undefined && id !== undefined && isValidDatasetName(dataset) && id.length > 0;
}
//# sourceMappingURL=schemas.js.map